import React, { useEffect, useState } from "react";

import { useRouter } from "next/router";
import GetTranslatedStrings from "@helpers/GetTranslatedStrings";
import styles from "./HorizontalAd.module.scss";
import { fetcherPublic1819 } from "@utils/fetcherPublic1819";
import { sanitizeUrl, sanitizeImageUrl } from "@helpers/urlValidation";
import { isAllowedAdDomain } from "@config/allowedAdDomains";

export default function HorizontalAd({ adPosition, className, style }) {
    if (!adPosition) {
        console.error("No ad position provided");
        return null;
    }
    const t = GetTranslatedStrings();
    const router = useRouter();
    const [ad, setAd] = useState(null);
    const [mounted, setMounted] = useState(true);
    const [sanitizedIframeCode, setSanitizedIframeCode] = useState("");

    const fetchAds = async () => {
        try {
            let query = router.query.q ? `&q=${router.query.q}` : "";
            let fetchedAd = await fetcherPublic1819(
                `1819/auglysing?placement=${adPosition}${query}`
            );
            if (mounted) {
                setAd(fetchedAd);
            }
        } catch (e) {
            console.error("Error fetching ad", e);
        }
    };

    useEffect(() => {
        fetchAds();

        return () => {
            setMounted(false);
        };
    }, [router.query.q]);

    // Sanitize iframe code on client-side only (DOMPurify requires browser environment)
    useEffect(() => {
        if (!ad?.iframe_code || typeof window === 'undefined') {
            setSanitizedIframeCode("");
            return;
        }

        // Dynamic import of DOMPurify to avoid SSR issues
        import('dompurify').then((DOMPurify) => {
            // Step 1: DOMPurify basic sanitization
            const cleaned = DOMPurify.default.sanitize(ad.iframe_code, {
                ALLOWED_TAGS: ["iframe"],
                ALLOWED_ATTR: ["src", "width", "height", "title", "frameborder", "allow", "allowfullscreen", "style"],
                ALLOWED_URI_REGEXP: /^https?:\/\//,
            });

            // Step 2: Domain whitelist validation and width adjustment
            const div = document.createElement('div');
            div.innerHTML = cleaned;
            const iframe = div.querySelector('iframe');

            if (iframe) {
                // Check domain whitelist
                if (iframe.src && !isAllowedAdDomain(iframe.src)) {
                    console.warn('Blocked iframe from non-whitelisted domain:', iframe.src);
                    setSanitizedIframeCode("");
                    return;
                }

                // Force iframe to take full width
                iframe.setAttribute('width', '100%');
                iframe.style.width = '100%';
                iframe.style.maxWidth = '100%';
                iframe.style.display = 'block';
                iframe.style.border = 'none';

                setSanitizedIframeCode(div.innerHTML);
            } else {
                setSanitizedIframeCode(cleaned);
            }
        }).catch((err) => {
            console.error('Failed to load DOMPurify:', err);
            setSanitizedIframeCode("");
        });
    }, [ad?.iframe_code]);

    // Sanitize URLs to prevent XSS attacks
    const safeImgUrl = sanitizeImageUrl(ad?.img_url);
    const safeLinkUrl = sanitizeUrl(ad?.linkto);

    // Determine if we should show iframe or image
    const shouldShowIframe = sanitizedIframeCode && !safeImgUrl;
    const shouldShowImage = safeImgUrl;

    // Handle click tracking for iframe ads
    const handleIframeClick = () => {
        if (safeLinkUrl && safeLinkUrl !== '#') {
            // Track click by calling linkto endpoint
            fetch(safeLinkUrl, { method: 'GET' }).catch(e => 
                console.error('Failed to track ad click:', e)
            );
        }
    };

    // Don't render anything if no ad content
    if (!shouldShowIframe && !shouldShowImage) {
        return null;
    }

    return (
        <div style={{ display: "flex", width: "100%", justifyContent: "center", ...style }}>
            <div
                className={className}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    maxWidth: 1018,
                }}
            >
                {shouldShowIframe && (
                    <div 
                        onClick={handleIframeClick}
                        className={styles.iframeWrapper}
                    >
                        <div
                            dangerouslySetInnerHTML={{ __html: sanitizedIframeCode }}
                        />
                    </div>
                )}
                
                {shouldShowImage && (
                    <a href={safeLinkUrl} target="_blank" rel="noopener noreferrer">
                        <picture style={{ aspectRatio: 1018 / 128 }}>
                            <img src={safeImgUrl} className={styles.container} alt={t.ad} />
                        </picture>
                    </a>
                )}
                
                {(shouldShowIframe || shouldShowImage) && (
                    <div
                        style={{
                            color: "gray",
                            fontSize: "0.75em",
                            marginTop: "0.5em",
                            alignSelf: "flex-end",
                        }}
                    >
                        {t.ad}
                    </div>
                )}
            </div>
        </div>
    );
}
